import { useAdminLogin } from '@fidalia/api';
import { Alert, Button, Field, Input } from '@fidalia/ui';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { MdLock, MdMail, MdVisibility, MdVisibilityOff } from 'react-icons/md';
import { useState } from 'react';
import { z } from 'zod';

import { AuthLayout } from '@/features/auth/auth-layout';
import { errorMessage } from '@/lib/auth';

const loginSchema = z.object({
  email: z
    .string()
    .trim()
    .min(1, 'Renseignez votre email.')
    .pipe(z.email('Adresse email invalide.')),
  password: z.string().min(1, 'Renseignez votre mot de passe.'),
});

type FieldErrors = Partial<Record<'email' | 'password', string>>;

export const Route = createFileRoute('/login')({
  beforeLoad: ({ context }) => {
    // Déjà un jeton : inutile de repasser par la connexion.
    if (context.tokens.get() !== null) throw redirect({ to: '/' });
  },
  component: LoginPage,
});

function LoginPage() {
  const navigate = useNavigate();
  const [values, setValues] = useState({ email: '', password: '' });
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [visible, setVisible] = useState(false);
  const login = useAdminLogin();

  const change = (name: 'email' | 'password') => (value: string) => {
    setFieldErrors((previous) => ({ ...previous, [name]: undefined }));
    setFormError(null);
    setValues((previous) => ({ ...previous, [name]: value }));
  };

  const submit = async () => {
    setFieldErrors({});
    setFormError(null);

    const result = loginSchema.safeParse(values);
    if (!result.success) {
      const errors: FieldErrors = {};
      for (const issue of result.error.issues) {
        const name = issue.path.at(0);
        if ((name === 'email' || name === 'password') && !errors[name]) {
          errors[name] = issue.message;
        }
      }
      setFieldErrors(errors);
      return;
    }

    try {
      await login.mutateAsync(result.data);
      await navigate({ to: '/' });
    } catch (error) {
      // Le backend ne distingue jamais « email inconnu » de « mauvais mot de
      // passe » : on affiche son message tel quel, en erreur de formulaire.
      setFormError(errorMessage(error, 'Connexion impossible.'));
    }
  };

  return (
    <AuthLayout title="Administration" subtitle="Connectez-vous à la console Fidalia.">
      {formError ? <Alert tone="danger">{formError}</Alert> : null}

      <form
        noValidate
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <Field label="Email" htmlFor="email" error={fieldErrors.email}>
          <Input
            id="email"
            name="email"
            type="email"
            autoComplete="username"
            autoFocus
            placeholder="prenom.nom@fidalia.fr"
            icon={<MdMail className="size-6" />}
            invalid={Boolean(fieldErrors.email || formError)}
            value={values.email}
            onChange={(event) => change('email')(event.target.value)}
          />
        </Field>

        <Field label="Mot de passe" htmlFor="password" error={fieldErrors.password}>
          <Input
            id="password"
            name="password"
            type={visible ? 'text' : 'password'}
            autoComplete="current-password"
            placeholder="••••••••••••"
            icon={<MdLock className="size-6" />}
            invalid={Boolean(fieldErrors.password || formError)}
            value={values.password}
            onChange={(event) => change('password')(event.target.value)}
            trailing={
              <button
                type="button"
                aria-label={visible ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                onClick={() => setVisible((current) => !current)}
                className="flex text-content-muted transition-colors hover:text-content"
              >
                {visible ? (
                  <MdVisibilityOff className="size-5" />
                ) : (
                  <MdVisibility className="size-5" />
                )}
              </button>
            }
          />
        </Field>

        <Button type="submit" size="lg" fullWidth loading={login.isPending} className="mt-2">
          Se connecter
        </Button>
      </form>

      <p className="text-center text-sm text-content-subtle">
        Mot de passe oublié ? Contactez un superadmin : les comptes sont créés à la main.
      </p>
    </AuthLayout>
  );
}
